import React, { useCallback, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { type IEnvironmentProvider, loadEntryPoint } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import ObjectIcon from '@atlaskit/icon-lab/core/assets-object';
import GoalIcon from '@atlaskit/icon/core/goal';
import PageIcon from '@atlaskit/icon/core/page';
import ProjectIcon from '@atlaskit/icon/core/project';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import TeamsIcon from '@atlaskit/icon/core/teams';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { functionWithCondition } from '@atlaskit/platform-feature-flags-react/function-with-condition/function-with-condition';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { AnalyticsAttributes } from '@atlassian/analytics-bridge';
import { AnyAri } from '@atlassian/ari/any-ari';
import type { PanelDataMap } from '@atlassian/confluence-page-preview-panel/constants';
import { confluencePagePreviewPanelEntryPoint } from '@atlassian/confluence-page-preview-panel/entrypoint';
import atlasGoalEntryPoint from '@atlassian/goal-panel/atlas-goal-entrypoint';
import objectPreviewPanelEntryPoint from '@atlassian/object-preview-panel/object-preview-panel-entrypoint';
import type { ActivityTabSlot } from '@atlassian/object-preview-panel/types';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { type PanelProduct, usePreviewPanelsActions } from '@atlassian/preview-panels-api';
import atlasProjectEntryPoint from '@atlassian/project-panel/atlas-project-entrypoint';
import { getDefaultSelectedTab } from '@atlassian/projects-goals-common/tabs';
import {
	AtlassianRelayEnvironmentProvider,
	useRelayEnvironmentProvider,
} from '@atlassian/relay-environment-provider';
import teamPreviewPanelEntryPoint from '@atlassian/team-preview-panel/team-preview-panel-entrypoint';

import { ALL_SUPPORTED_OBJECTS } from './constants';
import messages from './messages';
import { EntryPointPreviewPanel } from './panels/EntryPointPreviewPanel';
import { IFramePreviewPanel } from './panels/IFramePreviewPanel';
import type { GlobalObjectType } from './types';
import { isPreviewPanelUnitCheckEnabled } from './units-rollout';
import { useUnitIsolationEnabled } from './use-unit-isolation-enabled';
import { useUnitLinkedResources } from './use-unit-linked-resources';
import {
	buildIssueViewEmbedUrl,
	getGlobalObjectType,
	isInIframe,
	isPreviewPanelAvailable as isPreviewPanelAvailableForObject,
} from './utils';

export type Product = 'jira' | 'confluence' | 'teams' | 'home';

export type Options = {
	/**
	 * @deprecated Pass the slot per invocation via the second argument of `openPreviewPanel`
	 * ({@link OpenPreviewPanelOptions}) instead. Kept as a fallback for consumers that have not
	 * migrated yet; the per-open value takes precedence when both are supplied.
	 */
	activityTabSlot?: ActivityTabSlot;
	analyticsAttributes?: AnalyticsAttributes;
	enabled?: boolean;
	/**
	 * Optional override for the Relay environment provider used to load entry-point queries.
	 *
	 * By default, the closest `RelayEnvironmentProvider` in the React tree is used. Pass an
	 * explicit provider when the surrounding environment is not the AGG environment that the
	 * preview-panel entry-points expect (e.g. when sitting inside a product-specific Relay
	 * environment that does not support AGG persisted queries).
	 */
	environmentProvider?: IEnvironmentProvider<Record<string, never>>;
	/**
	 * Opt-in override for the maximum width the opened preview panel can be resized to, expressed in
	 * `vw` or `px` units (e.g. `'70vw'`). When omitted, the panel keeps the default cap of half the
	 * viewport width. Only the consumer that sets this gets the wider bound.
	 */
	maxWidth?: `${number}px` | `${number}vw`;
	rootSourceScreen?: string;
	supportedObjects?: GlobalObjectType[];
};

type IsPreviewPanelAvailable = (props: { ari: string }) => boolean;
type IsPreviewRestricted = (props: { ari: string }) => boolean;

type OpenPreviewPanelProps = {
	ari: string;
	name: string;
	onOpen?: () => void;
	organizationId?: string;
	panelData?: PanelDataMap;
	parentProduct?: string;
	url: string;
};

/**
 * Per-invocation configuration for {@link OpenPreviewPanel}. Takes precedence over the equivalent
 * hook-level {@link Options}, so a consumer can supply a different slot for every object it opens.
 */
export type OpenPreviewPanelOptions = {
	activityTabSlot?: ActivityTabSlot;
};

type OpenPreviewPanel = (props: OpenPreviewPanelProps, options?: OpenPreviewPanelOptions) => void;

export type useGlobalObjectPreviewPanelsReturnType = {
	isPreviewPanelAvailable: IsPreviewPanelAvailable;
	/**
	 * Whether previewing the resource is restricted because it is cross-unit. When
	 * `true`, callers should suppress the entire preview affordance (panel and modal).
	 */
	isPreviewRestricted: IsPreviewRestricted;
	openPreviewPanel: OpenPreviewPanel;
};

const parseGlobalObjectAri = (
	ari: string,
): { cloudId: string | undefined; globalObjectType: GlobalObjectType | undefined } => {
	try {
		const parsedAri = AnyAri.parse(ari);
		return {
			cloudId: parsedAri.cloudId || undefined,
			globalObjectType: `${parsedAri.resourceOwner}-${parsedAri.resourceType}` as GlobalObjectType,
		};
	} catch {
		return { cloudId: undefined, globalObjectType: undefined };
	}
};

/**
 * Stable stub for the legacy (gate-off) behaviour, where the cross-unit restriction signal does
 * not exist — always reports "not restricted" so nothing is suppressed. Declared at module scope
 * so it keeps a stable reference across renders (avoids invalidating consumers' `useMemo`s that
 * depend on `isPreviewRestricted`).
 */
const NOT_PREVIEW_RESTRICTED: IsPreviewRestricted = () => false;

export const useGlobalObjectPreviewPanelsNew = (
	product: Product,
	cloudId: string,
	options?: Options,
): useGlobalObjectPreviewPanelsReturnType => {
	const {
		enabled = true,
		activityTabSlot,
		supportedObjects = ALL_SUPPORTED_OBJECTS,
		analyticsAttributes,
		maxWidth,
		rootSourceScreen,
		environmentProvider: environmentProviderOverride,
	} = options ?? {};
	const defaultEnvironmentProvider = useRelayEnvironmentProvider();
	const environmentProvider = environmentProviderOverride ?? defaultEnvironmentProvider;
	const relayEnvironment = useMemo(
		() => environmentProvider.getEnvironment({}),
		[environmentProvider],
	);
	// Being in the rollout only decides that this implementation runs; whether cross-unit
	// resources are actually blocked additionally depends on the organisation having launched
	// units with boundary enforcement on, which is read from AGG.
	const isUnitIsolationEnabled = useUnitIsolationEnabled();
	const { isCloudIdInSameUnit } = useUnitLinkedResources(relayEnvironment, product, cloudId, {
		enabled: isUnitIsolationEnabled,
	});
	const { formatMessage } = useIntl();
	const { open } = usePreviewPanelsActions();

	// Shared object-type/enablement preconditions (no cross-unit check) so the
	// "available" and "restricted" signals cannot drift apart.
	const getPanelEligibility = useCallback(
		({ ari }: { ari: string }): { eligible: boolean; targetCloudId: string | undefined } => {
			const { globalObjectType: panelType, cloudId: targetCloudId } = parseGlobalObjectAri(ari);
			if (!panelType) {
				return { eligible: false, targetCloudId };
			}

			const panelEnabled = enabled && supportedObjects.includes(panelType);

			if (!panelEnabled) {
				return { eligible: false, targetCloudId };
			}

			return { eligible: true, targetCloudId };
		},
		[enabled, supportedObjects],
	);

	const isPreviewPanelAvailable = useCallback(
		({ ari }: { ari: string }): boolean => {
			const { eligible, targetCloudId } = getPanelEligibility({ ari });
			if (!eligible) {
				return false;
			}

			return isCloudIdInSameUnit(targetCloudId);
		},
		[getPanelEligibility, isCloudIdInSameUnit],
	);

	// True only for a panel-eligible object in a different unit.
	const isPreviewRestricted = useCallback(
		({ ari }: { ari: string }): boolean => {
			const { eligible, targetCloudId } = getPanelEligibility({ ari });
			if (!eligible) {
				return false;
			}

			return !isCloudIdInSameUnit(targetCloudId);
		},
		[getPanelEligibility, isCloudIdInSameUnit],
	);

	const openPreviewPanel = useCallback(
		(
			{
				url,
				ari,
				name,
				parentProduct,
				panelData,
				onOpen,
				organizationId = '',
			}: OpenPreviewPanelProps,
			openOptions?: OpenPreviewPanelOptions,
		): void => {
			let globalObjectType: GlobalObjectType | undefined;

			// Restricted (cross-unit) resources are valid types we decline to preview.
			if (isPreviewRestricted({ ari })) {
				return;
			}

			globalObjectType = parseGlobalObjectAri(ari).globalObjectType;
			if (!globalObjectType || !isPreviewPanelAvailable({ ari })) {
				throw new Error(
					`Preview panel for ${globalObjectType ?? 'unknown'} with ARI ${ari} is not supported.`,
				);
			}

			// Check if we're in an iframe (not the top-most frame)
			if (isInIframe()) {
				// Post message to parent frame to open the panel
				window.parent.postMessage(
					{
						type: 'OPEN_PREVIEW_PANEL',
						payload: {
							url,
							ari,
							name,
							panelData,
						},
					},
					'*',
				);
				return;
			}

			let panelContent;
			let panelProduct: PanelProduct;
			switch (globalObjectType) {
				case 'jira-issue':
					const JiraIcon = product === 'jira' ? WorkItemIcon : WorkItemsIcon;
					const title = formatMessage(messages.workItem);

					panelContent = (
						<IFramePreviewPanel
							name={title}
							url={url}
							iframeUrl={
								// Availability check only — this decides whether the embed URL carries the
								// rootSourceScreen, NOT whether the user is accessing the Capacity
								// planning experience. We therefore read the GA experiment WITHOUT
								// firing a Statsig exposure (fireExperimentExposure: false); exposure is
								// fired at the point the user actually opens the Capacity experience,
								// keeping the GA experiment's population clean.
								//
								// The OR still short-circuits: when the legacy EAP gate is ON the
								// experiment is never read at all. EAP customers remain unconditionally
								// enabled.
								fg('add_capacity_tab_to_the_board_horizontal_nav') ||
								FeatureGates.getExperimentValue(
									'individual_capacity_planning_ga',
									'isEnabled',
									false,
									{ fireExperimentExposure: false },
								)
									? buildIssueViewEmbedUrl(url, supportedObjects, rootSourceScreen, parentProduct)
									: buildIssueViewEmbedUrl(url, supportedObjects, undefined, parentProduct)
							}
							icon={<JiraIcon label="" />}
							panelType="jira-work-item"
							product="jira"
							parentProduct={parentProduct}
							ari={ari}
							// Availability check only — this decides whether to forward the
							// analyticsAttributes prop, NOT whether the user is accessing the Capacity
							// planning experience. Same no-exposure semantics as the iframeUrl prop
							// above: we read the GA experiment WITHOUT firing a Statsig exposure
							// (fireExperimentExposure: false). The OR still short-circuits when the
							// legacy EAP gate is ON; EAP customers remain unconditionally enabled.
							{...(fg('add_capacity_tab_to_the_board_horizontal_nav') ||
							FeatureGates.getExperimentValue(
								'individual_capacity_planning_ga',
								'isEnabled',
								false,
								{ fireExperimentExposure: false },
							)
								? { analyticsAttributes }
								: {})}
						/>
					);
					panelProduct = 'jira';
					break;
				case 'identity-team': {
					const teamEntryPointReference = loadEntryPoint<typeof teamPreviewPanelEntryPoint>(
						environmentProvider,
						teamPreviewPanelEntryPoint,
						{ cloudId },
					);
					const teamPanel = (
						<EntryPointPreviewPanel
							entryPoint={teamPreviewPanelEntryPoint}
							preloadedEntryPointReference={teamEntryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								ari,
								cloudId,
								openPreviewPanel,
							}}
							globalObject={{
								name: formatMessage(messages.team),
								url: url,
								icon: <TeamsIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						teamPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{teamPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'people';
					break;
				}
				case 'townsquare-goal': {
					const entryPointReference = loadEntryPoint<typeof atlasGoalEntryPoint>(
						environmentProvider,
						atlasGoalEntryPoint,
						{ ari },
					);
					const goalPanel = (
						<EntryPointPreviewPanel
							entryPoint={atlasGoalEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								cloudId,
								defaultTab: getDefaultSelectedTab(url),
							}}
							globalObject={{
								name: formatMessage(messages.goal),
								url: url,
								icon: <GoalIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						goalPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{goalPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'townsquare';
					break;
				}
				case 'townsquare-project': {
					const entryPointReference = loadEntryPoint<typeof atlasProjectEntryPoint>(
						environmentProvider,
						atlasProjectEntryPoint,
						{ ari },
					);
					const projectPanel = (
						<EntryPointPreviewPanel
							entryPoint={atlasProjectEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								cloudId,
								defaultTab: getDefaultSelectedTab(url),
							}}
							globalObject={{
								name: formatMessage(messages.project),
								url: url,
								icon: <ProjectIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						projectPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{projectPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'townsquare';
					break;
				}
				case 'confluence-blogpost':
				case 'confluence-page': {
					const isBlogPost = globalObjectType === 'confluence-blogpost';

					const urlObj = new URL(url);
					urlObj.searchParams.set(
						'previewPanels',
						encodeURIComponent(ALL_SUPPORTED_OBJECTS.join(',')),
					);
					const formattedUrl = urlObj.toString();
					const entryPointReference = loadEntryPoint<typeof confluencePagePreviewPanelEntryPoint>(
						environmentProvider,
						confluencePagePreviewPanelEntryPoint,
						{},
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={confluencePagePreviewPanelEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url: formattedUrl,
								parentProduct: product,
								contentType: isBlogPost ? 'blogpost' : 'page',
								panelData,
								cloudId,
							}}
							globalObject={{
								name: formatMessage(isBlogPost ? messages.blogPost : messages.page),
								url: formattedUrl,
								icon: isBlogPost ? <QuotationMarkIcon label="" /> : <PageIcon label="" />,
							}}
						/>
					);
					panelProduct = 'confluencePreviewPanel';
					break;
				}
				case 'confluence-whiteboard': {
					const entryPointReference = loadEntryPoint<typeof confluencePagePreviewPanelEntryPoint>(
						environmentProvider,
						confluencePagePreviewPanelEntryPoint,
						{},
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={confluencePagePreviewPanelEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
							}}
							globalObject={{
								name: formatMessage(messages.whiteboard),
								url: url,
								icon: <WhiteboardIcon label="" />,
							}}
						/>
					);
					panelProduct = 'confluencePreviewPanel';
					break;
				}
				case 'cmdb-object': {
					const assetsEntryPointReference = loadEntryPoint<typeof objectPreviewPanelEntryPoint>(
						environmentProvider,
						objectPreviewPanelEntryPoint,
						{ cloudId },
					);
					// Gate on: per-open slot wins, hook-level option is the fallback.
					// Gate off: keep the previous hook-level-only behavior.
					const resolvedActivityTabSlot = fg('roh_5641_preview_panel_per_open_activity_slot')
						? (openOptions?.activityTabSlot ?? activityTabSlot)
						: activityTabSlot;
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={objectPreviewPanelEntryPoint}
							preloadedEntryPointReference={assetsEntryPointReference}
							runtimeProps={{
								activityTabSlot: resolvedActivityTabSlot,
								url,
								parentProduct: product,
								ari,
								cloudId,
								organizationId,
								openPreviewPanel,
							}}
							// These are fallback properties
							globalObject={{
								name: formatMessage(messages.object),
								url: url,
								icon: <ObjectIcon label="" />,
							}}
						/>
					);
					panelProduct = 'assetsPreviewPanel';
					break;
				}
				default:
					throw new Error(`Unsupported global object type: ${globalObjectType}`);
			}

			open({
				panel: {
					id: ari,
					type: globalObjectType,
					content: () => panelContent,
					product: panelProduct,
					...(maxWidth && { maxWidth }),
				},
				...(fg('jivtech-1920-back-navigation-gpp') ? { onOpen } : {}),
			});
		},
		[
			isPreviewPanelAvailable,
			isPreviewRestricted,
			open,
			formatMessage,
			supportedObjects,
			environmentProvider,
			environmentProviderOverride,
			product,
			cloudId,
			activityTabSlot,
			analyticsAttributes,
			maxWidth,
			rootSourceScreen,
		],
	);

	return {
		isPreviewPanelAvailable,
		isPreviewRestricted,
		openPreviewPanel,
	};
};

export const useGlobalObjectPreviewPanelsOld = (
	product: Product,
	cloudId: string,
	options?: Options,
): useGlobalObjectPreviewPanelsReturnType => {
	const {
		enabled = true,
		activityTabSlot,
		supportedObjects = ALL_SUPPORTED_OBJECTS,
		analyticsAttributes,
		maxWidth,
		rootSourceScreen,
		environmentProvider: environmentProviderOverride,
	} = options ?? {};
	const defaultEnvironmentProvider = useRelayEnvironmentProvider();
	const environmentProvider = environmentProviderOverride ?? defaultEnvironmentProvider;
	const { formatMessage } = useIntl();
	const { open } = usePreviewPanelsActions();

	const isPreviewPanelAvailable = useCallback(
		({ ari }: { ari: string }): boolean => isPreviewPanelAvailableForObject(ari, enabled),
		[enabled],
	);

	const openPreviewPanel = useCallback(
		(
			{
				url,
				ari,
				name,
				parentProduct,
				panelData,
				onOpen,
				organizationId = '',
			}: OpenPreviewPanelProps,
			openOptions?: OpenPreviewPanelOptions,
		): void => {
			const globalObjectType = getGlobalObjectType(ari);

			if (!isPreviewPanelAvailable({ ari })) {
				throw new Error(`Preview panel for ${globalObjectType} with ARI ${ari} is not supported.`);
			}

			// Check if we're in an iframe (not the top-most frame)
			if (isInIframe()) {
				// Post message to parent frame to open the panel
				window.parent.postMessage(
					{
						type: 'OPEN_PREVIEW_PANEL',
						payload: {
							url,
							ari,
							name,
							panelData,
						},
					},
					'*',
				);
				return;
			}

			let panelContent;
			let panelProduct: PanelProduct;
			switch (globalObjectType) {
				case 'jira-issue':
					const JiraIcon = product === 'jira' ? WorkItemIcon : WorkItemsIcon;
					const title = formatMessage(messages.workItem);

					panelContent = (
						<IFramePreviewPanel
							name={title}
							url={url}
							iframeUrl={
								// Availability check only — this decides whether the embed URL carries the
								// rootSourceScreen, NOT whether the user is accessing the Capacity
								// planning experience. We therefore read the GA experiment WITHOUT
								// firing a Statsig exposure (fireExperimentExposure: false); exposure is
								// fired at the point the user actually opens the Capacity experience,
								// keeping the GA experiment's population clean.
								//
								// The OR still short-circuits: when the legacy EAP gate is ON the
								// experiment is never read at all. EAP customers remain unconditionally
								// enabled.
								fg('add_capacity_tab_to_the_board_horizontal_nav') ||
								FeatureGates.getExperimentValue(
									'individual_capacity_planning_ga',
									'isEnabled',
									false,
									{ fireExperimentExposure: false },
								)
									? buildIssueViewEmbedUrl(url, supportedObjects, rootSourceScreen, parentProduct)
									: buildIssueViewEmbedUrl(url, supportedObjects, undefined, parentProduct)
							}
							icon={<JiraIcon label="" />}
							panelType="jira-work-item"
							product="jira"
							parentProduct={parentProduct}
							ari={ari}
							// Availability check only — this decides whether to forward the
							// analyticsAttributes prop, NOT whether the user is accessing the Capacity
							// planning experience. Same no-exposure semantics as the iframeUrl prop
							// above: we read the GA experiment WITHOUT firing a Statsig exposure
							// (fireExperimentExposure: false). The OR still short-circuits when the
							// legacy EAP gate is ON; EAP customers remain unconditionally enabled.
							{...(fg('add_capacity_tab_to_the_board_horizontal_nav') ||
							FeatureGates.getExperimentValue(
								'individual_capacity_planning_ga',
								'isEnabled',
								false,
								{ fireExperimentExposure: false },
							)
								? { analyticsAttributes }
								: {})}
						/>
					);
					panelProduct = 'jira';
					break;
				case 'identity-team': {
					const teamEntryPointReference = loadEntryPoint<typeof teamPreviewPanelEntryPoint>(
						environmentProvider,
						teamPreviewPanelEntryPoint,
						{ cloudId },
					);
					const teamPanel = (
						<EntryPointPreviewPanel
							entryPoint={teamPreviewPanelEntryPoint}
							preloadedEntryPointReference={teamEntryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								ari,
								cloudId,
								openPreviewPanel,
							}}
							globalObject={{
								name: formatMessage(messages.team),
								url: url,
								icon: <TeamsIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						teamPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{teamPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'people';
					break;
				}
				case 'townsquare-goal': {
					const entryPointReference = loadEntryPoint<typeof atlasGoalEntryPoint>(
						environmentProvider,
						atlasGoalEntryPoint,
						{ ari },
					);
					const goalPanel = (
						<EntryPointPreviewPanel
							entryPoint={atlasGoalEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								cloudId,
								defaultTab: getDefaultSelectedTab(url),
							}}
							globalObject={{
								name: formatMessage(messages.goal),
								url: url,
								icon: <GoalIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						goalPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{goalPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'townsquare';
					break;
				}
				case 'townsquare-project': {
					const entryPointReference = loadEntryPoint<typeof atlasProjectEntryPoint>(
						environmentProvider,
						atlasProjectEntryPoint,
						{ ari },
					);
					const projectPanel = (
						<EntryPointPreviewPanel
							entryPoint={atlasProjectEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
								cloudId,
								defaultTab: getDefaultSelectedTab(url),
							}}
							globalObject={{
								name: formatMessage(messages.project),
								url: url,
								icon: <ProjectIcon label="" />,
							}}
						/>
					);
					panelContent = environmentProviderOverride ? (
						projectPanel
					) : (
						<AtlassianRelayEnvironmentProvider>{projectPanel}</AtlassianRelayEnvironmentProvider>
					);
					panelProduct = 'townsquare';
					break;
				}
				case 'confluence-blogpost':
				case 'confluence-page': {
					const isBlogPost = globalObjectType === 'confluence-blogpost';

					const urlObj = new URL(url);
					urlObj.searchParams.set(
						'previewPanels',
						encodeURIComponent(ALL_SUPPORTED_OBJECTS.join(',')),
					);
					const formattedUrl = urlObj.toString();
					const entryPointReference = loadEntryPoint<typeof confluencePagePreviewPanelEntryPoint>(
						environmentProvider,
						confluencePagePreviewPanelEntryPoint,
						{},
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={confluencePagePreviewPanelEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url: formattedUrl,
								parentProduct: product,
								contentType: isBlogPost ? 'blogpost' : 'page',
								panelData,
								cloudId,
							}}
							globalObject={{
								name: formatMessage(isBlogPost ? messages.blogPost : messages.page),
								url: formattedUrl,
								icon: isBlogPost ? <QuotationMarkIcon label="" /> : <PageIcon label="" />,
							}}
						/>
					);
					panelProduct = 'confluencePreviewPanel';
					break;
				}
				case 'confluence-whiteboard': {
					const entryPointReference = loadEntryPoint<typeof confluencePagePreviewPanelEntryPoint>(
						environmentProvider,
						confluencePagePreviewPanelEntryPoint,
						{},
					);
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={confluencePagePreviewPanelEntryPoint}
							preloadedEntryPointReference={entryPointReference}
							runtimeProps={{
								url,
								parentProduct: product,
							}}
							globalObject={{
								name: formatMessage(messages.whiteboard),
								url: url,
								icon: <WhiteboardIcon label="" />,
							}}
						/>
					);
					panelProduct = 'confluencePreviewPanel';
					break;
				}
				case 'cmdb-object': {
					const assetsEntryPointReference = loadEntryPoint<typeof objectPreviewPanelEntryPoint>(
						environmentProvider,
						objectPreviewPanelEntryPoint,
						{ cloudId },
					);
					// Gate on: per-open slot wins, hook-level option is the fallback.
					// Gate off: keep the previous hook-level-only behavior.
					const resolvedActivityTabSlot = fg('roh_5641_preview_panel_per_open_activity_slot')
						? (openOptions?.activityTabSlot ?? activityTabSlot)
						: activityTabSlot;
					panelContent = (
						<EntryPointPreviewPanel
							entryPoint={objectPreviewPanelEntryPoint}
							preloadedEntryPointReference={assetsEntryPointReference}
							runtimeProps={{
								activityTabSlot: resolvedActivityTabSlot,
								url,
								parentProduct: product,
								ari,
								cloudId,
								organizationId,
								openPreviewPanel,
							}}
							// These are fallback properties
							globalObject={{
								name: formatMessage(messages.object),
								url: url,
								icon: <ObjectIcon label="" />,
							}}
						/>
					);
					panelProduct = 'assetsPreviewPanel';
					break;
				}
				default:
					throw new Error(`Unsupported global object type: ${globalObjectType}`);
			}

			open({
				panel: {
					id: ari,
					type: globalObjectType,
					content: () => panelContent,
					product: panelProduct,
					...(maxWidth && { maxWidth }),
				},
				...(fg('jivtech-1920-back-navigation-gpp') ? { onOpen } : {}),
			});
		},
		[
			isPreviewPanelAvailable,
			open,
			formatMessage,
			supportedObjects,
			environmentProvider,
			environmentProviderOverride,
			product,
			cloudId,
			activityTabSlot,
			analyticsAttributes,
			maxWidth,
			rootSourceScreen,
		],
	);

	return {
		isPreviewPanelAvailable,
		isPreviewRestricted: NOT_PREVIEW_RESTRICTED,
		openPreviewPanel,
	};
};

const isAllowedOrigin = (origin: string): boolean => {
	return origin === window.location.origin;
};

export const usePreviewPanelIFrameCommunication = (openPreviewPanel: OpenPreviewPanel): void => {
	// Listen for messages from iframes to open panels
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			// Validate origin FIRST - reject messages from untrusted origins
			if (!isAllowedOrigin(event.origin)) {
				return;
			}

			// Only handle messages with the expected type
			if (event.data?.type !== 'OPEN_PREVIEW_PANEL') {
				return;
			}

			const { payload } = event.data;
			if (payload?.url && payload?.ari && payload?.name) {
				openPreviewPanel({
					url: payload.url,
					ari: payload.ari,
					name: payload.name,
					panelData: payload.panelData,
				});
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [openPreviewPanel]);
};

/**
 * Returns helpers for opening global-object preview panels.
 *
 * Behaviour is fully swapped behind the units isolation rollout gates (see
 * {@link isPreviewPanelUnitCheckEnabled}: the `cc-units-ga` killswitch, then either of preview
 * panels' own org id and cloud id targeted gates):
 * - in the rollout  → {@link useGlobalObjectPreviewPanelsNew} (cross-unit aware; exposes
 *   `isPreviewRestricted` and suppresses previews for resources in a different unit, once AGG
 *   confirms the organisation has launched units with boundary enforcement on).
 * - not in the rollout → {@link useGlobalObjectPreviewPanelsOld} (legacy behaviour;
 *   `isPreviewRestricted` always returns `false`).
 *
 * Those are session-constant feature gates, so the selected implementation does not change while
 * a consumer is mounted. The organisation's units state is deliberately *not* part of this
 * condition: it is resolved asynchronously from AGG inside the cross-unit aware implementation,
 * which fails open until it is known.
 */
export const useGlobalObjectPreviewPanels: (
	product: Product,
	cloudId: string,
	options?: Options,
) => useGlobalObjectPreviewPanelsReturnType = functionWithCondition(
	isPreviewPanelUnitCheckEnabled,
	useGlobalObjectPreviewPanelsNew,
	useGlobalObjectPreviewPanelsOld,
);
